/**
 * legal — the legal-document STRUCTURE: the shape both the Terms of Service
 * and the Privacy Policy render into, the two builders that assemble a
 * `LegalDocument` from this slice's own `legal.terms.*` / `legal.privacy.*`
 * catalog keys plus an operator's `LegalIdentity`, and the operator-override
 * layer that lets a deployment publish its own text instead: the stored
 * shape (`LegalContentSchema`), the total line-classifier from source text
 * to sections (`blocksFromSource`) and its own inverse back to source text
 * (`sourceFromBlocks` — what lets an AI-formatted draft land in the same
 * editable textarea a paste fills), and the resolver that picks the override
 * over the platform default (`resolveLegalDocument`). Whether an operator's
 * own text actually discharges its statutory duties is not decided here:
 * that judgment requires reading the whole composed document, and belongs to
 * the `reviewLegalDocument` flow (`@momo/types`'s `ReviewLegalDocument.*`).
 *
 * A typed content module, not markdown: the two documents live as DE/EN
 * twins in the i18n catalog, so no bundler loader, no fetch, and no runtime
 * parse stands between the consent screen and the text it must show. A
 * consumer imports `TERMS` / `PRIVACY`, calls the locale's builder with a
 * `LegalIdentity`, and renders sections — it never owns a sentence of legal
 * prose itself. An operator's own published text instead reaches the same
 * `LegalDocument` shape through `resolveLegalDocument`, parsed from stored
 * source text at render time rather than carried as a second representation.
 *
 * THE IDENTITY RULE: not one field of `LegalIdentity` may appear as a
 * literal anywhere in this i18n slice. CLAUDE.md's one hard boundary bars a
 * tracked file from naming a deployment, and `pnpm check`'s seam scan
 * enforces it — the operator's name, address, VAT id, register entry,
 * representative, contact address, and domain arrive at render time off the
 * public workspace card (`workspaces/{orgId}`, hydrated into the
 * `workspaceCard` state singleton) plus `CLIENT.domain`, never a literal
 * here. The catalog carries `{{param}}` tokens; `translate`/`translateList`
 * interpolate them from the identity the caller supplies. The operator's own
 * published document text is a different case: it lives in Firestore, not
 * tracked source, so it may — and does — name the deployment's own identity.
 *
 * The withdrawal-right exclusion (§ 312g Abs. 2 Nr. 9 BGB, Terms clause 7)
 * and the consumer-arbitration declaration (§ 36 VSBG, Terms clause 14), plus
 * the checkout point-of-sale duties this identity feeds
 * (`checkout-disclosures.view.ts`), are recorded and decided in
 * docs/samwa-legal/legal-review-register.md — L1, L2, L11–L14.
 *
 * `PROCESSORS` / `deploymentProcessors` / `deploymentRecipientsSection` are
 * the privacy document's recipients half: a tracked registry of every third
 * party this deployment's own code can reach, filtered by
 * `DeploymentCapabilities` — the wallet/maps/Meta switches a deployment
 * has actually turned on — and rendered as the SAME generated section whether the
 * platform's own default document (`buildPrivacy`) or an operator's override
 * (`resolveLegalDocument`) is what's showing, so the two can never disclose a
 * different recipient set. An override's own text can never omit this
 * section, because the section is appended after the override rather than
 * something the override could ever have contained. Evidenced against the
 * running code per entry (L7, L20); the Veranstalter/Vermittler agency
 * distinction those findings touch on stays out of this file, absent a data
 * model carrier for it (L25) — a disclosure the software cannot make is
 * worse than silence. The Hausrecht/admission (Terms s6), transfer/resale
 * (Terms s5) and recording (Terms s6) content folded in below is generic to
 * ticketing, not to one operator's business.
 */

import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import {
	type Locale,
	type MessageKey,
	translate,
	translateList,
} from "./translate.ts";
import { PRIVACY_VERSION, TOS_VERSION } from "./versions.ts";

/**
 * The operator identity a German legal document must state, supplied by the
 * renderer. Every field maps to a live source; none may be defaulted, and an
 * empty string must never reach a rendered document.
 *
 *   brandName      — Workspace.brand.name
 *   legalName      — Workspace.legal.legalName
 *   address        — Workspace.legal.address   (one line, street/postcode/city/country)
 *   vatId          — Workspace.legal.vatId     (USt-IdNr., § 27a UStG)
 *   register       — Workspace.legal.register  (Registergericht + HRB)
 *   representative — Workspace.legal.representative (vertretungsberechtigte Person)
 *   contactEmail   — Workspace.legal.contactEmail (§ 5 Abs. 1 Nr. 2 DDG's rapid-contact
 *                    address — NOT Workspace.email.sender, which is the transactional
 *                    From address ticket and receipt emails send from, never a contact
 *                    address a buyer or authority reaches a human through)
 *   domain         — CLIENT.domain             (the storefront host)
 */
export type LegalIdentity = {
	brandName: string;
	legalName: string;
	address: string;
	vatId: string;
	register: string;
	representative: string;
	contactEmail: string;
	domain: string;
};

/** A paragraph, or a bulleted list — the only two block forms either document needs. */
export type LegalBlock =
	| { kind: "p"; text: string }
	| { kind: "list"; items: string[] };

export type LegalSection = {
	heading: string;
	blocks: LegalBlock[];
};

/**
 * `version` is stamped from `TOS_VERSION`/`PRIVACY_VERSION` by the builder,
 * so the text a member accepts and the string recorded in
 * `users/{uid}.legalConsents[].version` can never drift apart.
 */
export type LegalDocument = {
	title: string;
	version: string;
	sections: LegalSection[];
};

/** What a locale's document builder is: identity in, finished document out. */
export type LegalDocumentBuilder = (identity: LegalIdentity) => LegalDocument;

function identityParams(id: LegalIdentity): Record<string, string> {
	return {
		brandName: id.brandName,
		legalName: id.legalName,
		address: id.address,
		vatId: id.vatId,
		register: id.register,
		representative: id.representative,
		contactEmail: id.contactEmail,
		domain: id.domain,
	};
}

function p(
	locale: Locale,
	key: MessageKey,
	params: Record<string, string>,
): LegalBlock {
	return { kind: "p", text: translate(locale, key, params) };
}

function list(locale: Locale, key: MessageKey): LegalBlock {
	return { kind: "list", items: translateList(locale, key) };
}

function section(
	locale: Locale,
	headingKey: MessageKey,
	blocks: LegalBlock[],
): LegalSection {
	return { heading: translate(locale, headingKey), blocks };
}

// ── Processor registry ──────────────────────────────────────────────────────
// The set of recipients a privacy policy must name is a property of the
// software, not of the operator's business — it changes when the platform
// adds an integration, not when an operator edits their document. PROCESSORS
// is the tracked, code-level source of that set, and
// deploymentRecipientsSection below is what every privacy document — default
// or override — renders it through.

/**
 * ProcessorRole — Art. 28 GDPR binds a controller→processor engagement; it
 * does not reach a recipient that determines its own purposes over the data
 * it receives. `"processor"` is the Art. 28 default every entry here assumes
 * absent a documented reason otherwise — the shipped assumption
 * `legal.privacy.s4.processorsNote` already states. `"independentController"`
 * is reserved for an entry a register item has actually examined against its
 * own governing terms: `psp` (already the shipped position) and
 * `wallet-google` (L20 — Google's own Additional ToS reserve its own usage
 * rights over submitted pass data and route through the Google
 * Controller-Controller Data Protection Terms, not the Cloud DPA).
 * `"jointController"` is the Art. 26 case: a recipient the platform shares
 * the determination of purposes and means with, rather than one that merely
 * receives what the platform decided alone — both Meta entries, which Meta's
 * own Controller Addendum governs (register L32). `meta-ads-browser-tag` is
 * the *Fashion ID* posture in full: the embedded tag performs the collection
 * and the transmission jointly. `meta-ads-server-report` embeds nothing, so
 * Fashion ID's reasoning does not reach it — the Art. 26 arrangement there is
 * the contractual one the Controller Addendum applies to a Business-Tools
 * transmission however it arrives, and that entry's own text scopes the joint
 * responsibility to the transmission rather than to a collection Meta takes
 * no part in. The role's own rendered sentence
 * (`legal.privacy.s4.jointControllerNote`) carries the generic Art. 26
 * grammar and names the clause-1 controller as the visitor's contact
 * point; the entry's `purposeKey` carries the arrangement's essence.
 */
export type ProcessorRole =
	| "processor"
	| "independentController"
	| "jointController";

/** A deployment switch a non-`always` PROCESSORS entry gates on — see
 *  `DeploymentCapabilities` for the raw fact each one reads. */
export type ProcessorCapability =
	| "walletGoogle"
	| "walletApple"
	| "maps"
	| "metaAdsServerReport"
	| "metaAdsBrowserTag";

/**
 * DeploymentCapabilities — the facts `deploymentProcessors` gates a
 * capability-scoped entry on. Both wallet fields are booleans, not the
 * underlying identifiers, and deliberately so: the privacy document that
 * reads this is served to an ANONYMOUS buyer (the checkout overlay, the
 * public `/legal` page), and `WorkspaceSettings.wallet` — the private
 * singleton `googleIssuerId`/`applePassTypeId`/`appleTeamId` actually live
 * on — is read-gated behind `tickets.events.manage` + mfa
 * (`firestore.rules`); a buyer's session can never resolve it, so a
 * capabilities shape built from it would always evaluate to "nothing
 * configured" on the one surface where the disclosure matters most. The
 * correct source is `Workspace.wallet.googleEnabled` /
 * `.appleEnabled` — the SAME public workspace card the checkout/legal
 * surfaces already hold in hand, carrying only the derived boolean (a
 * capability, not a credential), kept true by a Firestore trigger mirroring
 * `settings/private` rather than by any hand-maintained copy (see that
 * entity field's own doc in `packages/types/src/workspace.entity.ts`).
 * `mapsApiKey` is different in kind: `CLIENT.maps.apiKey` is a browser-
 * bundled config value, already public by construction (the same class as
 * `firebase.apiKey`) — no mirror is needed for it.
 * `metaPixelId` and `metaBrowserTag` ride the same public card as the wallet
 * booleans — `Workspace.ads.meta.pixelId`, written only by the Admin SDK in
 * saveIntegration's register hook once the access token has proved it can
 * read that pixel, and `Workspace.ads.meta.browserTag`, the operator's own
 * second decision on top of it — so the anonymous buyer's session that
 * renders this document already holds both, and the disclosure and the
 * storefront tag gate on the one same pair of facts.
 *
 * Two facts, never one: with the tag off, Meta's only reach into this
 * deployment is one server-side report per completed purchase, and the
 * visitor's device is untouched — no script, no cookie, no read. A document
 * that derived a cookie disclosure from the pixel id alone would then state
 * a storage that does not happen, which is worse than omitting it. Both
 * optional rather than required, so a caller not yet wired with the card's
 * `ads` map keeps compiling and degrades to the same under-disclosure
 * default as an absent capability.
 */
export type DeploymentCapabilities = {
	readonly walletGoogleEnabled: boolean;
	readonly walletAppleEnabled: boolean;
	readonly mapsApiKey: string | null;
	readonly metaPixelId?: string | null;
	readonly metaBrowserTag?: boolean;
};

/** Nothing beyond the platform's own unconditional recipients — every
 *  capability-gated entry stays hidden. The default for every caller that
 *  hasn't been wired with live facts yet: an unwired call site
 *  under-discloses rather than over-discloses, since an absent recipient is
 *  a caller yet to be wired, while a named recipient receiving nothing is
 *  the L20 inaccuracy this registry exists to end. */
export const NO_CAPABILITIES: DeploymentCapabilities = {
	walletGoogleEnabled: false,
	walletAppleEnabled: false,
	mapsApiKey: null,
	metaPixelId: null,
	metaBrowserTag: false,
};

type ProcessorEntryFields = {
	/** Stable, never rendered — for tests, cross-referencing the legal
	 *  register, and future tooling (an editor surfacing this registry). */
	readonly id: string;
	/** The recipient's own name — documentation only, English-stable since
	 *  a legal entity's name doesn't translate. The rendered list item's
	 *  actual text lives whole, per locale, in `purposeKey`'s own catalog
	 *  value; this field is never concatenated into it, so a locale-specific
	 *  connector ("bzw." vs "/") or a generic non-proper-noun descriptor
	 *  (`psp` below, which names no fixed legal entity) is authored
	 *  correctly in each language rather than forced through one label. */
	readonly vendor: string;
	readonly role: ProcessorRole;
	/** The catalog key whose DE/EN value IS the complete rendered list item
	 *  for this entry — vendor name and purpose in one authored sentence,
	 *  exactly like every list item this registry replaces. */
	readonly purposeKey: MessageKey;
	/** Whether this entry's own transfer reaches outside the EEA — gates
	 *  `legal.privacy.s4.thirdCountryNote`'s inclusion. */
	readonly thirdCountry: boolean;
};

/** A PROCESSORS entry is live unconditionally, or only once a deployment has
 *  actually turned on the capability it depends on — never both, so no
 *  entry can carry a stale `capability` alongside a since-added `always`. */
export type ProcessorEntry =
	| (ProcessorEntryFields & { readonly always: true })
	| (ProcessorEntryFields & { readonly capability: ProcessorCapability });

/**
 * PROCESSORS — one entry per third party this deployment's own code can
 * actually reach. Populated against the running code, not the prose it
 * replaces (L7's own method). An entry gated by `capability` renders only
 * once that capability is actually live (`deploymentProcessors`) — never
 * unconditionally, per the legal register's own finding that naming a
 * recipient receiving nothing is itself an inaccuracy (L20).
 *
 * The PDF/ticket-image renderer (`@momo/pdf-render`, invoked from
 * `receipt.webhook.ts` / `ticket-files.webhook.ts`) earns no entry of its
 * own: it is a Cloud Run service inside this SAME GCP project,
 * IAM-authenticated (Google-minted ID tokens, no public ingress —
 * `pdf-render/README.md`), covered by the identical Google Cloud
 * Data Processing Addendum the `google-cloud` entry below already discloses
 * for Cloud Functions — not a distinct third party. `google-cloud`'s own
 * purpose text names the rendering explicitly rather than silently omitting
 * it.
 */
export const PROCESSORS = [
	{
		id: "google-cloud",
		vendor: "Google Ireland Limited / Google LLC",
		role: "processor",
		purposeKey: "legal.privacy.processors.googleCloud",
		thirdCountry: true,
		always: true,
	},
	{
		id: "gemini",
		vendor: "Google Ireland Limited / Google LLC",
		role: "processor",
		purposeKey: "legal.privacy.processors.gemini",
		thirdCountry: true,
		// Unconditional: every server-side call into this vendor —
		// generateEventArtwork's image generation
		// (firebase/src/events/generateArtwork.api.ts) and
		// formatLegalDocument's processing of an operator's own pasted
		// legal-document text (firebase/src/legal/formatLegalDocument.chat.ts)
		// — ships on every build, gated only by permission + mfa, each with
		// its own required `geminiApiKey` secret binding. No deployment flag
		// ships without at least one of them, and purposeKey (above) states
		// the PURPOSE this entry discloses rather than enumerating callables,
		// so a future addition reaching this same vendor for the same kind
		// of purpose needs no matching edit here to stay true.
		always: true,
	},
	{
		id: "lettermint",
		vendor: "Lettermint B.V.",
		role: "processor",
		purposeKey: "legal.privacy.processors.lettermint",
		// Not independently confirmed EU-only; kept true (conservative)
		// rather than asserted EU-resident on no evidence — matches this
		// section's own blanket third-country sentence.
		thirdCountry: true,
		always: true,
	},
	{
		id: "psp",
		// No single fixed legal entity: Workspace.payment.provider
		// (packages/types/src/workspace.entity.ts) is a switch, Revolut is
		// the only wired option today (packages/config/src/schema.ts), and
		// purposeKey's own text stays provider-neutral rather than
		// asserting an entity this repo cannot verify — L7's own posture,
		// and already the shipped wording.
		vendor: "the checkout's payment provider",
		role: "independentController", // already the shipped position; purposeKey states it inline
		purposeKey: "legal.privacy.processors.psp",
		thirdCountry: true,
		always: true, // Workspace.payment.provider is required, never nullish
	},
	{
		id: "maps",
		vendor: "Google Ireland Limited / Google LLC",
		role: "processor",
		purposeKey: "legal.privacy.processors.maps",
		thirdCountry: true,
		// Gated on the admin embed's own API key, not on the storefront's
		// unconditional "get directions" link: that link is a plain URL
		// (events.state.ts's `mapsUrl`) the BUYER may click, transmitting
		// nothing themselves — not the software reaching a recipient.
		//
		// Admin-only, and disclosed anyway: this entry names no buyer data —
		// it belongs in the shared privacy document because STAFF are data
		// subjects of that same document too (s1.p2: it covers "both the
		// public ticket shop and the internal administration interface"),
		// and loading the Maps JS API inside the admin surface transmits the
		// STAFF member's own browser/IP data to Google, not a buyer's. One
		// document serves both audiences by design (resolveLegalDocument has
		// no staff-only variant), so this entry stays in the one list.
		capability: "maps",
	},
	{
		id: "wallet-google",
		vendor: "Google Ireland Limited / Google LLC",
		role: "independentController", // L20
		purposeKey: "legal.privacy.processors.walletGoogle",
		thirdCountry: true,
		capability: "walletGoogle",
	},
	{
		id: "wallet-apple",
		vendor: "Apple Inc.",
		// Provisional Art. 28 default — no register item has examined
		// Apple's own governing terms the way L20 examined Google's. The
		// data reaching Apple is minimal (a device push token + the pass
		// type identifier as the APNs topic, no ticket or buyer content —
		// walletUpdate.webhook.ts), and this entry is only live once an
		// operator has configured both wallet identifiers.
		role: "processor",
		purposeKey: "legal.privacy.processors.walletApple",
		thirdCountry: true,
		capability: "walletApple",
	},
	{
		id: "meta-ads-server-report",
		vendor: "Meta Platforms Ireland Limited",
		// The tier a registered pixel id alone puts a deployment in: the
		// server posts one purchase event per paid order and the storefront
		// loads nothing. purposeKey's text therefore names a hashed e-mail,
		// an order value and an order identifier leaving our own server, and
		// names no cookie, no browsing behaviour and no device data — none of
		// which exist with no tag to produce them. It states the Art. 6(1)(f)
		// basis with the standing Art. 21 objection clause 3 carries, the
		// Art. 26 arrangement's essence (Meta's Controller Addendum, scoped
		// to the transmission), and the Art. 13(1)(f) onward transfer to Meta
		// Platforms, Inc. under its EU-US Data Privacy Framework
		// certification. The objection route it names is a message to the
		// operator's contact address, which holds wherever this row is
		// appended — an operator's own published policy included, where no
		// surrounding clause of ours survives to carry it.
		//
		// TWO PURPOSES ON ONE TRANSMISSION, AND NO SECOND CAPABILITY FOR THE
		// SECOND. An ad set built here binds delivery optimisation to this
		// same pixel (`promoted_object`), so the purchase events already
		// leaving under this row also decide who is shown the ads next — a
		// widened PURPOSE on an unchanged payload and an unchanged recipient.
		// Art. 13(1)(c) puts that in this row's own text, which is where it
		// is; a capability of its own would gate a THIRD Meta row on a fact
		// no public workspace card carries, against `metaAdsTier`'s own
		// claim that exactly one Meta row is ever live. What that second
		// purpose may rest on is register item L37's, not this file's — the
		// text describes the processing and leaves the basis sentence
		// stating the interest it states.
		role: "jointController",
		purposeKey: "legal.privacy.processors.metaAdsServerReport",
		thirdCountry: true,
		capability: "metaAdsServerReport",
	},
	{
		id: "meta-ads-browser-tag",
		vendor: "Meta Platforms Ireland Limited",
		// The tier the operator's own second toggle opens: Meta's tag runs in
		// the shop on top of the server report, so purposeKey's text adds the
		// cookie identifiers, the browsing funnel and the IP/browser
		// identification the tag itself transmits, and rests the whole
		// processing on the visitor's consent. Mutually exclusive with the
		// server-report entry above (`capabilityLive`), so exactly one Meta
		// row is ever live and the two can never both describe one
		// deployment.
		role: "jointController",
		purposeKey: "legal.privacy.processors.metaAdsBrowserTag",
		thirdCountry: true,
		capability: "metaAdsBrowserTag",
	},
] as const satisfies ReadonlyArray<ProcessorEntry>;

/** Which of Meta's two reaches into a deployment is the live one, from the
 *  one pair of public-card facts that decides it. A closed set of three
 *  rather than the raw pair, because only three of the four combinations
 *  mean anything: with no registered pixel id the tag flag says nothing at
 *  all, and a boolean per tier leaves the impossible both-tiers state
 *  representable at every call site that reads them. */
export type MetaAdsTier = "none" | "serverReport" | "browserTag";

/**
 * metaAdsTier — the PROCESSING fact, and the one place the pair is read.
 * `"serverReport"` is what a pasted pixel id alone produces: our own server
 * posts one purchase event per paid order and the storefront loads nothing.
 * `"browserTag"` is the operator's own second decision on top of it, where
 * Meta's script runs in the shop as well and the whole processing rests on
 * the visitor's consent.
 *
 * The one source both readers of the pair share: `capabilityLive` below
 * picks which recipient row a deployment discloses, and
 * `<checkout-disclosures>` (`packages/views/src/storefront/`) gates the
 * Art. 13 cue it renders where the buyer's e-mail is actually typed. The
 * two can never disagree about which tier a deployment is in.
 */
export function metaAdsTier(
	capabilities: Pick<DeploymentCapabilities, "metaPixelId" | "metaBrowserTag">,
): MetaAdsTier {
	if (capabilities.metaPixelId == null) return "none";
	return capabilities.metaBrowserTag === true ? "browserTag" : "serverReport";
}

function capabilityLive(
	capability: ProcessorCapability,
	capabilities: DeploymentCapabilities,
): boolean {
	switch (capability) {
		case "walletGoogle":
			return capabilities.walletGoogleEnabled;
		case "walletApple":
			return capabilities.walletAppleEnabled;
		case "maps":
			return capabilities.mapsApiKey != null;
		// The two Meta tiers are mutually exclusive, so a registered pixel id
		// discloses Meta either way and the operator's own browser-tag toggle
		// decides which of the two rows is the true one.
		case "metaAdsServerReport":
			return metaAdsTier(capabilities) === "serverReport";
		case "metaAdsBrowserTag":
			return metaAdsTier(capabilities) === "browserTag";
	}
}

/**
 * deploymentProcessors — PROCESSORS filtered to what this deployment has
 * actually switched on: every `always` entry, plus every `capability` entry
 * whose own switch is live in `capabilities`. `capabilities` defaults to
 * `NO_CAPABILITIES` so a caller not yet wired with live facts degrades to
 * under- rather than over-disclosure.
 */
export function deploymentProcessors(
	capabilities: DeploymentCapabilities = NO_CAPABILITIES,
): readonly ProcessorEntry[] {
	return PROCESSORS.filter((entry) =>
		"always" in entry
			? entry.always
			: capabilityLive(entry.capability, capabilities),
	);
}

/**
 * deploymentRecipientsSection — the ONE recipients/processors section every
 * privacy document carries: `buildPrivacy`'s own section 4, and the section
 * `resolveLegalDocument` appends after an operator's own override — both
 * routes call this same function, so the two can never disclose a different
 * recipient set. The bulleted list is `deploymentProcessors`'s live entries,
 * one translated list item per entry; the Art. 28 sentence, its
 * independent-controller and joint-controller counterparts, and the
 * third-country sentence are each included only when a live entry's own
 * `role`/`thirdCountry` actually calls for it — never printed
 * unconditionally, the same discipline the list itself follows.
 */
export function deploymentRecipientsSection(
	locale: Locale,
	capabilities: DeploymentCapabilities = NO_CAPABILITIES,
): LegalSection {
	const entries = deploymentProcessors(capabilities);
	const blocks: LegalBlock[] = [
		p(locale, "legal.privacy.s4.p1", {}),
		{
			kind: "list",
			items: entries.map((entry) => translate(locale, entry.purposeKey)),
		},
	];
	if (entries.some((entry) => entry.role === "processor")) {
		blocks.push(p(locale, "legal.privacy.s4.processorsNote", {}));
	}
	if (entries.some((entry) => entry.role === "independentController")) {
		blocks.push(p(locale, "legal.privacy.s4.independentControllerNote", {}));
	}
	if (entries.some((entry) => entry.role === "jointController")) {
		blocks.push(p(locale, "legal.privacy.s4.jointControllerNote", {}));
	}
	if (entries.some((entry) => entry.thirdCountry)) {
		blocks.push(p(locale, "legal.privacy.s4.thirdCountryNote", {}));
	}
	blocks.push(p(locale, "legal.privacy.s4.p3", {}));
	return section(locale, "legal.privacy.s4.heading", blocks);
}

function buildTerms(locale: Locale, id: LegalIdentity): LegalDocument {
	const t = identityParams(id);
	return {
		title: translate(locale, "legal.terms.title"),
		version: TOS_VERSION,
		sections: [
			section(locale, "legal.terms.s1.heading", [
				p(locale, "legal.terms.s1.p1", t),
				p(locale, "legal.terms.s1.p2", t),
				p(locale, "legal.terms.s1.p3", t),
			]),
			section(locale, "legal.terms.s2.heading", [
				p(locale, "legal.terms.s2.p1", t),
				p(locale, "legal.terms.s2.p2", t),
				p(locale, "legal.terms.s2.p3", t),
			]),
			section(locale, "legal.terms.s3.heading", [
				p(locale, "legal.terms.s3.p1", t),
				p(locale, "legal.terms.s3.p2", t),
				p(locale, "legal.terms.s3.p3", t),
			]),
			section(locale, "legal.terms.s4.heading", [
				p(locale, "legal.terms.s4.p1", t),
				p(locale, "legal.terms.s4.p2", t),
				p(locale, "legal.terms.s4.p3", t),
				p(locale, "legal.terms.s4.p4", t),
			]),
			section(locale, "legal.terms.s5.heading", [
				p(locale, "legal.terms.s5.p1", t),
				p(locale, "legal.terms.s5.p2", t),
			]),
			section(locale, "legal.terms.s6.heading", [
				p(locale, "legal.terms.s6.p1", t),
				p(locale, "legal.terms.s6.p2", t),
				p(locale, "legal.terms.s6.p3", t),
			]),
			section(locale, "legal.terms.s7.heading", [
				p(locale, "legal.terms.s7.p1", t),
				p(locale, "legal.terms.s7.p2", t),
			]),
			section(locale, "legal.terms.s8.heading", [
				p(locale, "legal.terms.s8.p1", t),
				p(locale, "legal.terms.s8.p2", t),
			]),
			section(locale, "legal.terms.s9.heading", [
				p(locale, "legal.terms.s9.p1", t),
				list(locale, "legal.terms.s9.list1"),
				p(locale, "legal.terms.s9.p2", t),
			]),
			section(locale, "legal.terms.s10.heading", [
				p(locale, "legal.terms.s10.p1", t),
			]),
			section(locale, "legal.terms.s11.heading", [
				p(locale, "legal.terms.s11.p1", t),
				p(locale, "legal.terms.s11.p2", t),
				p(locale, "legal.terms.s11.p3", t),
			]),
			section(locale, "legal.terms.s12.heading", [
				p(locale, "legal.terms.s12.p1", t),
			]),
			section(locale, "legal.terms.s13.heading", [
				p(locale, "legal.terms.s13.p1", t),
			]),
			section(locale, "legal.terms.s14.heading", [
				p(locale, "legal.terms.s14.p1", t),
				p(locale, "legal.terms.s14.p2", t),
				p(locale, "legal.terms.s14.p3", t),
			]),
		],
	};
}

function buildPrivacy(
	locale: Locale,
	id: LegalIdentity,
	capabilities: DeploymentCapabilities = NO_CAPABILITIES,
): LegalDocument {
	const t = identityParams(id);
	return {
		title: translate(locale, "legal.privacy.title"),
		version: PRIVACY_VERSION,
		sections: [
			section(locale, "legal.privacy.s1.heading", [
				p(locale, "legal.privacy.s1.p1", t),
				p(locale, "legal.privacy.s1.p2", t),
			]),
			section(locale, "legal.privacy.s2.heading", [
				p(locale, "legal.privacy.s2.p1", t),
				p(locale, "legal.privacy.s2.p2", t),
				list(locale, "legal.privacy.s2.list1"),
				p(locale, "legal.privacy.s2.p3", t),
				list(locale, "legal.privacy.s2.list2"),
				p(locale, "legal.privacy.s2.p4", t),
			]),
			section(locale, "legal.privacy.s3.heading", [
				list(locale, "legal.privacy.s3.list1"),
				// The server report's purpose, basis and objection route. Its
				// basis is Art. 6(1)(f), so the visitor is owed the Art. 21 route
				// the switch at the end of this clause carries — the same
				// disclosure the usage measurement in the list above makes, though
				// that one names the switch beneath it and this one names the
				// contact address, because the switch ends the measurement alone.
				// The browser tier states its own basis and withdrawal route at its
				// recipient row and the clause-6 cookie line instead, because there
				// the processing rests on consent.
				...(capabilityLive("metaAdsServerReport", capabilities)
					? [p(locale, "legal.privacy.s3.adsReport", t)]
					: []),
				p(locale, "legal.privacy.s3.p1", t),
			]),
			deploymentRecipientsSection(locale, capabilities),
			section(locale, "legal.privacy.s5.heading", [
				list(locale, "legal.privacy.s5.list1"),
				p(locale, "legal.privacy.s5.p1", t),
			]),
			section(locale, "legal.privacy.s6.heading", [
				p(locale, "legal.privacy.s6.p1", t),
				// `_fbp`/`_fbc` exist only where the browser tag runs, so this
				// line gates on the tag and never on the pixel id: a server-only
				// deployment sets no cookie, and a document claiming one states
				// a storage that does not happen.
				...(capabilityLive("metaAdsBrowserTag", capabilities)
					? [p(locale, "legal.privacy.s6.metaCookies", t)]
					: []),
			]),
			section(locale, "legal.privacy.s7.heading", [
				list(locale, "legal.privacy.s7.list1"),
				p(locale, "legal.privacy.s7.p1", t),
				p(locale, "legal.privacy.s7.p2", t),
			]),
			section(locale, "legal.privacy.s8.heading", [
				p(locale, "legal.privacy.s8.p1", t),
				p(locale, "legal.privacy.s8.p2", t),
			]),
			section(locale, "legal.privacy.s9.heading", [
				p(locale, "legal.privacy.s9.p1", t),
			]),
		],
	};
}

export const TERMS: Record<Locale, LegalDocumentBuilder> = {
	de: (id) => buildTerms("de", id),
	en: (id) => buildTerms("en", id),
};

/** `PRIVACY`'s own builder shape, widened over `LegalDocumentBuilder` by one
 *  optional `capabilities` parameter — the recipients section
 *  (`deploymentRecipientsSection`) is the one part of a privacy document that
 *  isn't a pure function of identity alone. Optional, not required: every
 *  existing caller of `PRIVACY[locale](identity)` keeps compiling and
 *  degrades to `NO_CAPABILITIES`'s under-disclosure default until it is
 *  wired with live facts. */
export type PrivacyDocumentBuilder = (
	identity: LegalIdentity,
	capabilities?: DeploymentCapabilities,
) => LegalDocument;

export const PRIVACY: Record<Locale, PrivacyDocumentBuilder> = {
	de: (id, capabilities) => buildPrivacy("de", id, capabilities),
	en: (id, capabilities) => buildPrivacy("en", id, capabilities),
};

// ── Operator override ───────────────────────────────────────────────────────
// The platform documents above are the floor — they render synchronously
// from tracked catalog text, so no surface is ever blank while a Firestore
// read is pending. Everything below lets a deployment publish its own text
// in their place, one kind (terms/privacy) at a time, both locales at once.

/** The one closed vocabulary for which legal document a stored piece of
 *  content, a version, a resolver call, or a compliance review is about —
 *  shared by `LegalContentSchema`'s siblings, `@momo/types`'s `LegalVersion`
 *  entity (`kind`) and its `ReviewLegalDocument` contract, so no package
 *  keeps its own copy of the same two-value set. */
export const LegalDocumentKindSchema = z.enum(["terms", "privacy"]);
export type LegalDocumentKind = z.infer<typeof LegalDocumentKindSchema>;

/**
 * LegalContentSchema — the stored shape of `workspaces/{orgId}/settings/legal`
 * (`@momo/types`'s `LegalContent` entity, `packages/types/src/workspace.entity.ts`):
 * one document, both kinds, both locales, written atomically on publish. A
 * kind is fully published only once its own `version` and BOTH locale texts
 * are present — `resolveLegalDocument` below is the sole reader of that
 * rule. Source text only, never a parsed representation: storing both would
 * double the size against Firestore's 1 MiB doc cap and let the two drift;
 * `blocksFromSource` re-parses at render instead, bounded by the `.max()`
 * below.
 *
 * Fields justified:
 *   terms/privacy.version — the operator's own version stamp for that kind,
 *     recorded onto the resolved `LegalDocument` and, from there, onto a
 *     consent record — distinct from the platform's own TOS_VERSION /
 *     PRIVACY_VERSION, which a resolved override never carries.
 *   terms/privacy.{de,en} — the operator's source text for that kind and
 *     locale, in `blocksFromSource`'s line convention. A kind's version and
 *     both locale texts are written together in one publish, but each is its
 *     own nullish field rather than a single required blob, so a doc that
 *     has never been published for a kind is still schema-valid (every
 *     field absent) rather than needing a placeholder value.
 *   publishedAt — when the most recent publish (either kind) wrote this
 *     document; nullish before any publish has ever happened, exactly like
 *     the fields it stamps.
 * Excluded:
 *   publishedBy — who published lives on the archive entry
 *     (`@momo/types`'s `LegalVersion`) instead; the public doc every
 *     anonymous buyer reads never carries a member uid.
 *   a rendered/parsed representation — see above.
 */
export const LegalContentSchema = z.object({
	terms: z.object({
		version: z
			.string()
			.min(1)
			.nullish()
			.describe(
				"the operator's own version stamp for their published Terms of Service — nullish while the operator has never published an override for this kind",
			),
		de: z
			.string()
			.max(65536)
			.nullish()
			.describe(
				"the operator's German-language Terms of Service source text, in blocksFromSource's line convention (# heading, - list item, blank line ends a paragraph) — nullish while unpublished",
			),
		en: z
			.string()
			.max(65536)
			.nullish()
			.describe(
				"the operator's English-language Terms of Service source text, in blocksFromSource's line convention — nullish while unpublished",
			),
	}),
	privacy: z.object({
		version: z
			.string()
			.min(1)
			.nullish()
			.describe(
				"the operator's own version stamp for their published Privacy Policy — nullish while the operator has never published an override for this kind",
			),
		de: z
			.string()
			.max(65536)
			.nullish()
			.describe(
				"the operator's German-language Privacy Policy source text, in blocksFromSource's line convention — nullish while unpublished",
			),
		en: z
			.string()
			.max(65536)
			.nullish()
			.describe(
				"the operator's English-language Privacy Policy source text, in blocksFromSource's line convention — nullish while unpublished",
			),
	}),
	publishedAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"when the most recent publish — either kind — wrote this document; nullish before any publish has ever happened",
		),
});
export type LegalContent = z.infer<typeof LegalContentSchema>;

/**
 * blocksFromSource — the TOTAL line-classifier from an operator's pasted
 * source text to typed sections: a `#`-prefixed line starts a new section
 * (the rest of the line, trimmed, is its heading); consecutive `- `-prefixed
 * lines accumulate into one list block; a blank line ends the paragraph in
 * progress; every other line is paragraph text, joined with the rest of its
 * paragraph by a single space. Total: no input line is ever dropped, and
 * text preceding the first heading collects into a leading section with an
 * empty heading rather than being discarded — a parser that silently drops
 * an unsupported construct will one day drop a liability clause. Pure, no
 * dependency, no markdown library: bold, links and tables are simply not
 * expressible, by design (the Solution table's own decision).
 */
export function blocksFromSource(text: string): LegalSection[] {
	const sections: LegalSection[] = [];
	let current: LegalSection | null = null;
	let paragraphLines: string[] = [];
	let listItems: string[] | null = null;

	const ensureSection = (): LegalSection => {
		if (!current) {
			current = { heading: "", blocks: [] };
			sections.push(current);
		}
		return current;
	};
	const flushParagraph = () => {
		if (paragraphLines.length === 0) return;
		ensureSection().blocks.push({ kind: "p", text: paragraphLines.join(" ") });
		paragraphLines = [];
	};
	const flushList = () => {
		if (!listItems || listItems.length === 0) return;
		ensureSection().blocks.push({ kind: "list", items: listItems });
		listItems = null;
	};

	for (const line of text.split("\n")) {
		if (line.startsWith("#")) {
			flushParagraph();
			flushList();
			current = { heading: line.slice(1).trim(), blocks: [] };
			sections.push(current);
		} else if (line.startsWith("- ")) {
			flushParagraph();
			if (!listItems) listItems = [];
			listItems.push(line.slice(2));
		} else if (line.trim() === "") {
			flushParagraph();
			flushList();
		} else {
			flushList();
			paragraphLines.push(line);
		}
	}
	flushParagraph();
	flushList();

	return sections;
}

/**
 * sourceFromBlocks — `blocksFromSource`'s own inverse: typed sections back
 * into an operator-editable source string, in `blocksFromSource`'s own line
 * convention. This is what lets `formatLegalDocument`'s AI output (raw
 * paste → `LegalSection[]`) land back in the SAME textarea an operator's own
 * paste fills — reviewable and re-editable, not a second representation the
 * store text can drift from.
 *
 * Every block is one line — a paragraph's `text` verbatim, a list's `items`
 * each `- `-prefixed and newline-joined — and every block, the section's own
 * `# heading` line included, is joined to its neighbour by a blank line.
 * That blank line is always safe, never merely convenient: a blank line
 * only resets `blocksFromSource`'s in-progress paragraph/list accumulation,
 * it never starts a section the way a `#` line does, so inserting one
 * between any two blocks (matching kind or not) can only separate them,
 * never merge or split anything else. The alternative — omitting it between
 * two same-kind blocks — is precisely what would collapse two distinct `p`
 * blocks into `blocksFromSource`'s multi-line-paragraph join on re-parse, so
 * every join here is unconditional, not simplified for the common case.
 *
 * Round-trip fidelity — `blocksFromSource(sourceFromBlocks(sections))`
 * deep-equals `sections` — is this function's whole reason to exist, proven
 * over the platform's own four shipped documents in `legal.test.ts`. It
 * relies on one property `blocksFromSource`'s line convention itself
 * imposes and this function cannot enforce: no block's text may itself
 * start a line with `#` or `- `, and no block's text may carry a literal
 * newline (blocksFromSource has no per-block escaping — that would be lossy
 * in the other direction). True of every block this codebase produces
 * today (the shipped catalog text, and `formatLegalDocument`'s own prompt,
 * which is instructed to preserve prose rather than invent Markdown); not
 * structurally guaranteed for arbitrary `LegalSection[]` input.
 */
export function sourceFromBlocks(sections: LegalSection[]): string {
	return sections
		.map((section) => {
			const blockLines = section.blocks.map((block) =>
				block.kind === "p"
					? block.text
					: block.items.map((item) => `- ${item}`).join("\n"),
			);
			return [`# ${section.heading}`, ...blockLines].join("\n\n");
		})
		.join("\n\n");
}

/**
 * resolveLegalDocument — the one function every reader (the checkout
 * overlay, the public `/legal` page, the staff consent screen) resolves a
 * document through. Returns the operator's override, built from stored
 * source text via `blocksFromSource`, only once `kind` is FULLY published in
 * `content` — its own version and BOTH locale texts present; otherwise
 * returns the platform's own `TERMS`/`PRIVACY` builder for `locale`. There is
 * deliberately no cross-locale fallback: a kind is all-or-nothing, because an
 * English-addressed consumer shown German-only terms fails § 305 Abs. 2
 * Nr. 2 BGB, and a mixed operator-DE/platform-EN pair would be two
 * substantively different contracts. Synchronous, no I/O — `content`,
 * `identity` and `capabilities` are already resolved by the caller.
 *
 * A published privacy override is the operator's own parsed sections
 * followed by `deploymentRecipientsSection` — appended, never merged into
 * what the operator wrote, because the override's own text never contained
 * it and so can never omit it. This is what makes the recipients disclosure
 * structurally survive an override rather than depend on a publish-time
 * check: the override was never asked to carry it in the first place.
 */
export function resolveLegalDocument(
	kind: LegalDocumentKind,
	locale: Locale,
	content: LegalContent,
	identity: LegalIdentity,
	capabilities: DeploymentCapabilities = NO_CAPABILITIES,
): LegalDocument {
	const { version, de, en } = content[kind];
	if (version == null || de == null || en == null) {
		return kind === "terms"
			? TERMS[locale](identity)
			: PRIVACY[locale](identity, capabilities);
	}
	if (kind === "terms") {
		return {
			title: translate(locale, "legal.terms.title"),
			version,
			sections: blocksFromSource(locale === "de" ? de : en),
		};
	}
	return {
		title: translate(locale, "legal.privacy.title"),
		version,
		sections: [
			...blocksFromSource(locale === "de" ? de : en),
			deploymentRecipientsSection(locale, capabilities),
		],
	};
}
